'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { InputMask } from '@react-input/mask';
import FormInputError from '@/app/_components/FormInputError';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
import GooglePlacesAutocomplete from '@/app/_components/GooglePlacesAutocomplete';
import PasswordField from '@/app/_components/PasswordField';
import { useReCaptcha } from 'next-recaptcha-v3';
import getBillingStatesAction from '@/app/_lib/actions/GetBillingStatesAction';
import getShippingStatesAction from '@/app/_lib/actions/GetShippingStatesAction';
import createAccountAction from '@/app/_lib/actions/CreateAccountAction';
import { trackSignUp } from '@/app/_lib/analytics/ga4';

// Zod schema for account creation
const createAccountSchema = z.object({
  account_id: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  shipping_first_name: z.string().min(1, 'First name is required'),
  shipping_last_name: z.string().min(1, 'Last name is required'),
  shipping_company_name: z.string().optional(),
  shipping_street_1: z.string().min(1, 'Street address is required'),
  shipping_street_2: z.string().optional(),
  shipping_city: z.string().min(1, 'City is required'),
  shipping_state: z.string().min(1, 'State is required'),
  shipping_zip: z.string().min(5, 'ZIP code is required'),
  shipping_phone: z.string().min(14, 'Phone number is required'),
  alternate_shipping_phone: z.string().optional(),
  billing_same_as_shipping: z.boolean(),
  billing_first_name: z.string().optional(),
  billing_last_name: z.string().optional(),
  billing_company_name: z.string().optional(),
  billing_street_1: z.string().optional(),
  billing_street_2: z.string().optional(),
  billing_city: z.string().optional(),
  billing_state: z.string().optional(),
  billing_zip: z.string().optional(),
  billing_phone: z.string().optional(),
  alt_billing_phone: z.string().optional()
}).refine(data => {
  // If billing is different, require billing fields
  if (!data.billing_same_as_shipping) {
    return data.billing_first_name && data.billing_last_name && data.billing_street_1 && data.billing_city && data.billing_state && data.billing_zip && data.billing_phone;
  }
  return true;
}, {
  message: 'Billing address fields are required',
  path: ['billing_first_name']
});

// Strip leading country code (1 or +1) from phone numbers for autocomplete compatibility
const phoneTrack = ({
  inputType,
  data
}) => {
  if (inputType === 'insert' && data) {
    let digits = data.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.slice(1);
    }
    return digits;
  }
  return data;
};
const CreateAccountForm = ({
  className
}) => {
  const router = useRouter();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const [billingStates, setBillingStates] = useState([]);
  const [shippingStates, setShippingStates] = useState([]);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [serverError, setServerError] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      account_id: '',
      shipping_first_name: '',
      shipping_last_name: '',
      shipping_company_name: '',
      shipping_street_1: '',
      shipping_street_2: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
      shipping_phone: '',
      alternate_shipping_phone: '',
      billing_same_as_shipping: true,
      billing_first_name: '',
      billing_last_name: '',
      billing_company_name: '',
      billing_street_1: '',
      billing_street_2: '',
      billing_city: '',
      billing_state: '',
      billing_zip: '',
      billing_phone: '',
      alt_billing_phone: ''
    }
  });
  const billingSameAsShipping = watch('billing_same_as_shipping');

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      const billingResponse = await getBillingStatesAction();
      if (billingResponse.success && billingResponse.states) {
        setBillingStates(billingResponse.states);
      }
      const shippingResponse = await getShippingStatesAction();
      if (shippingResponse.success && shippingResponse.states) {
        setShippingStates(shippingResponse.states);
      }
    };
    fetchStates();
  }, []);

  // Google Places callbacks - update form fields when address is selected
  const handleShippingPlaceSelected = addressData => {
    setValue('shipping_city', addressData.city);
    setValue('shipping_state', addressData.state);
    setValue('shipping_zip', addressData.zip);
  };
  const handleBillingPlaceSelected = addressData_0 => {
    setValue('billing_city', addressData_0.city);
    setValue('billing_state', addressData_0.state);
    setValue('billing_zip', addressData_0.zip);
  };
  const onSubmit = async data => {
    setServerError(null);
    setServerErrors({});

    // Get reCAPTCHA token
    const recaptchaToken = await executeRecaptcha('create_account');
    const requestData = {
      recaptchaToken: recaptchaToken,
      account_id: data.account_id,
      password_1: passwordData.password,
      password_2: passwordData.confirmPassword,
      // Shipping information
      shipping_first_name: data.shipping_first_name,
      shipping_last_name: data.shipping_last_name,
      shipping_company_name: data.shipping_company_name,
      shipping_street_1: data.shipping_street_1,
      shipping_street_2: data.shipping_street_2,
      shipping_city: data.shipping_city,
      shipping_state: data.shipping_state,
      shipping_zip: data.shipping_zip,
      shipping_phone: data.shipping_phone,
      // Billing information (conditional)
      billing_same_as_shipping: data.billing_same_as_shipping,
      billing_first_name: data.billing_same_as_shipping ? data.shipping_first_name : data.billing_first_name,
      billing_last_name: data.billing_same_as_shipping ? data.shipping_last_name : data.billing_last_name,
      billing_company_name: data.billing_same_as_shipping ? data.shipping_company_name : data.billing_company_name,
      billing_street_1: data.billing_same_as_shipping ? data.shipping_street_1 : data.billing_street_1,
      billing_street_2: data.billing_same_as_shipping ? data.shipping_street_2 : data.billing_street_2,
      billing_city: data.billing_same_as_shipping ? data.shipping_city : data.billing_city,
      billing_state: data.billing_same_as_shipping ? data.shipping_state : data.billing_state,
      billing_zip: data.billing_same_as_shipping ? data.shipping_zip : data.billing_zip,
      billing_phone: data.billing_same_as_shipping ? data.shipping_phone : data.billing_phone,
      alt_billing_phone: data.billing_same_as_shipping ? data.alternate_shipping_phone : data.alt_billing_phone
    };
    const response = await createAccountAction(requestData);
    if (response.success === true) {
      trackSignUp();
      // Sign in via NextAuth with the same credentials
      // Wrap in timeout to prevent infinite hang
      try {
        const signInPromise = signIn('credentials', {
          email: data.account_id,
          password: passwordData.password,
          redirect: false
        });
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('SIGNIN_TIMEOUT')), 30000));
        const signInResult = await Promise.race([signInPromise, timeoutPromise]);
        if (signInResult?.ok) {
          // Navigate to account - session change triggers context update automatically
          router.push('/account');
        } else {
          // Account created but sign-in failed - redirect to login
          router.push('/account/login?message=Account created. Please sign in.');
        }
      } catch (error) {
        // Account created but sign-in timed out - redirect to login
        router.push('/account/login?message=Account created. Please sign in.');
      }
    } else {
      setServerError(response.message);
      setServerErrors(response.errors || {});
    }
  };

  // Combine RHF errors with server errors
  const getError = field => errors[field]?.message || serverErrors[field];
  return <div className={`w-full ${className}`}>
            <form onSubmit={handleSubmit(onSubmit)}>
                    {serverError && <div role="alert" aria-live="assertive" className="text-red font-medium p-3 border-l-4 border-red mb-4">
                            {serverError}
                        </div>}

                    {/* Contact Section */}
                    <div className="mb-8 rounded">

                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-medium text-base">Contact Information</h2>
                            <Link href="/account/login" className="text-sm text-primary font-medium">Sign In</Link>
                        </div>

                        <FormInputError id="account_id-error" message={getError('account_id')} />
                        <div className="form-group mb-4">
                            <input id="account_id" type="email" autoComplete="email" className="form-input peer" placeholder=" " required aria-invalid={!!getError('account_id')} aria-describedby={getError('account_id') ? "account_id-error" : undefined} {...register('account_id')} />
                            <label htmlFor="account_id" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Email <span className="text-primary">*</span>
                            </label>
                        </div>

                        <PasswordField name="password_1" confirmName="password_2" showConfirm={true} serverErrors={serverErrors} onChange={setPasswordData} className="mb-4" />


                        <h2 className="font-medium text-base mb-1">Shipping Address</h2>
                        <div className="text-xs text-gray-500 mb-5"><span className="text-black">Please Note:</span> We cannot accept orders for Alaska and Hawaii. International orders are not accepted for online purchases.</div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <FormInputError id="shipping_first_name-error" message={getError('shipping_first_name')} />
                                <div className="form-group">
                                    <input id="shipping_first_name" type="text" autoComplete="given-name" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_first_name')} aria-describedby={getError('shipping_first_name') ? "shipping_first_name-error" : undefined} {...register('shipping_first_name')} />
                                    <label htmlFor="shipping_first_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        First Name <span className="text-primary">*</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <FormInputError id="shipping_last_name-error" message={getError('shipping_last_name')} />
                                <div className="form-group">
                                    <input id="shipping_last_name" type="text" autoComplete="family-name" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_last_name')} aria-describedby={getError('shipping_last_name') ? "shipping_last_name-error" : undefined} {...register('shipping_last_name')} />
                                    <label htmlFor="shipping_last_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Last Name <span className="text-primary">*</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <FormInputError id="shipping_company_name-error" message={getError('shipping_company_name')} />
                                <div className="form-group">
                                    <input id="shipping_company_name" type="text" autoComplete="organization" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_company_name')} aria-describedby={getError('shipping_company_name') ? "shipping_company_name-error" : undefined} {...register('shipping_company_name')} />
                                    <label htmlFor="shipping_company_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Company Name
                                    </label>
                                </div>
                            </div>
                        </div>

                        <FormInputError id="shipping_street_1-error" message={getError('shipping_street_1')} />
                        <Controller name="shipping_street_1" control={control} render={({
          field: field_0
        }) => <GooglePlacesAutocomplete id="shipping_street_1" name="shipping_street_1" label="Shipping Street Address *" value={field_0.value} onChange={e => field_0.onChange(e.target.value)} onPlaceSelected={handleShippingPlaceSelected} required ariaProps={{
          'aria-required': 'true',
          'aria-invalid': !!getError('shipping_street_1'),
          'aria-describedby': getError('shipping_street_1') ? "shipping_street_1-error" : undefined
        }} />} />
                        <p className="text-2xs -mt-2 mb-4">We cannot ship to PO Boxes</p>

                        <FormInputError id="shipping_street_2-error" message={getError('shipping_street_2')} />
                        <div className="form-group mb-4">
                            <input id="shipping_street_2" type="text" autoComplete="address-line2" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_street_2')} aria-describedby={getError('shipping_street_2') ? "shipping_street_2-error" : undefined} {...register('shipping_street_2')} />
                            <label htmlFor="shipping_street_2" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                Shipping Address (Line 2)
                            </label>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div>
                                <FormInputError id="shipping_city-error" message={getError('shipping_city')} />
                                <div className="form-group">
                                    <input id="shipping_city" type="text" autoComplete="address-level2" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_city')} aria-describedby={getError('shipping_city') ? "shipping_city-error" : undefined} {...register('shipping_city')} />
                                    <label htmlFor="shipping_city" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        City <span className="text-primary">*</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <FormInputError id="shipping_state-error" message={getError('shipping_state')} />
                                <div className="form-group">
                                    <Controller name="shipping_state" control={control} render={({
                field: field_1
              }) => <>
                                                <select id="shipping_state" autoComplete="address-level1" className="form-input peer" aria-invalid={!!getError('shipping_state')} aria-describedby={getError('shipping_state') ? "shipping_state-error" : undefined} {...field_1}>
                                                    <option value=""></option>
                                                    {shippingStates.map(state => <option key={state.state_id} value={state.abbr}>
                                                            {state.state_name}
                                                        </option>)}
                                                </select>
                                                <label htmlFor="shipping_state" className={`form-input-label ${!field_1.value ? 'form-input-label-placeholder-shown' : ''}`}>
                                                    State <span className="text-primary">*</span>
                                                </label>
                                            </>} />
                                </div>
                            </div>

                            <div>
                                <FormInputError id="shipping_zip-error" message={getError('shipping_zip')} />
                                <div className="form-group">
                                    <input id="shipping_zip" type="text" autoComplete="postal-code" inputMode="numeric" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_zip')} aria-describedby={getError('shipping_zip') ? "shipping_zip-error" : undefined} {...register('shipping_zip')} />
                                    <label htmlFor="shipping_zip" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                        Zip Code <span className="text-primary">*</span>
                                    </label>
                                </div>
                            </div>


                        </div>
                        <div className="flex flex-col flex-col-1 md:flex-row gap-2 mb-1">
                            <div className=" w-full">
                                <FormInputError id="shipping_phone-error" message={getError('shipping_phone')} />
                                <div className="form-group">
                                    <Controller name="shipping_phone" control={control} render={({
                field: field_2
              }) => <InputMask mask="(___) ___-____" replacement={{
                _: /\d/
              }} track={phoneTrack} id="shipping_phone" type="tel" className="form-input peer" placeholder=" " aria-invalid={!!getError('shipping_phone')} aria-describedby={getError('shipping_phone') ? "shipping_phone-error" : undefined} autoComplete="tel" inputMode="tel" value={field_2.value} onChange={field_2.onChange} onBlur={field_2.onBlur} />} />
                                    <label htmlFor="shipping_phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Phone <span className="text-primary">*</span></label>
                                </div>
                            </div>
                            <div className=" w-full">
                                <FormInputError id="alternate_shipping_phone-error" message={getError('alternate_shipping_phone')} />
                                <div className="form-group">
                                    <Controller name="alternate_shipping_phone" control={control} render={({
                field: field_3
              }) => <InputMask mask="(___) ___-____" replacement={{
                _: /\d/
              }} track={phoneTrack} id="alternate_shipping_phone" type="tel" className="form-input peer" placeholder=" " aria-invalid={!!getError('alternate_shipping_phone')} aria-describedby={getError('alternate_shipping_phone') ? "alternate_shipping_phone-error" : undefined} autoComplete="tel" inputMode="tel" value={field_3.value} onChange={field_3.onChange} onBlur={field_3.onBlur} />} />
                                    <label htmlFor="alternate_shipping_phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Alternate Phone</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Billing Same as Shipping Checkbox */}
                    <div className="flex items-center gap-3 mb-6">
                        <input type="checkbox" id="billing_same_as_shipping" className="checkbox-switch" {...register('billing_same_as_shipping')} />
                        <label htmlFor="billing_same_as_shipping">Billing address is the same as shipping address</label>
                    </div>

                    {/* Billing Information Section (Conditional) */}
                    <div className={`grid overflow-hidden transition-all duration-300 ease-in-out
                            ${!billingSameAsShipping ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                        <div className="overflow-hidden">
                            <div className="mb-8">
                                <h3 className="text-xl font-light mb-4">Billing Information</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <FormInputError id="billing_first_name-error" message={getError('billing_first_name')} />
                                    <div className="form-group mb-4">
                                        <input id="billing_first_name" type="text" autoComplete="given-name" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_first_name')} aria-describedby={getError('billing_first_name') ? "billing_first_name-error" : undefined} {...register('billing_first_name')} />
                                        <label htmlFor="billing_first_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                            First Name {!billingSameAsShipping && <span className="text-primary">*</span>}
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <FormInputError id="billing_last_name-error" message={getError('billing_last_name')} />
                                    <div className="form-group mb-4">
                                        <input id="billing_last_name" type="text" autoComplete="family-name" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_last_name')} aria-describedby={getError('billing_last_name') ? "billing_last_name-error" : undefined} {...register('billing_last_name')} />
                                        <label htmlFor="billing_last_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                            Last Name {!billingSameAsShipping && <span className="text-primary">*</span>}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <FormInputError id="billing_company_name-error" message={getError('billing_company_name')} />
                            <div className="form-group mb-4">
                                <input id="billing_company_name" type="text" autoComplete="organization" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_company_name')} aria-describedby={getError('billing_company_name') ? "billing_company_name-error" : undefined} {...register('billing_company_name')} />
                                <label htmlFor="billing_company_name" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                    Company Name
                                </label>
                            </div>

                            <FormInputError id="billing_street_1-error" message={getError('billing_street_1')} />
                            <Controller name="billing_street_1" control={control} render={({
              field: field_4
            }) => <GooglePlacesAutocomplete id="billing_street_1" name="billing_street_1" label={`Street Address${!billingSameAsShipping ? ' *' : ''}`} value={field_4.value} onChange={e_0 => field_4.onChange(e_0.target.value)} onPlaceSelected={handleBillingPlaceSelected} required={!billingSameAsShipping} ariaProps={{
              'aria-required': !billingSameAsShipping ? 'true' : undefined,
              'aria-invalid': !!getError('billing_street_1'),
              'aria-describedby': getError('billing_street_1') ? "billing_street_1-error" : undefined
            }} />} />

                            <FormInputError id="billing_street_2-error" message={getError('billing_street_2')} />
                            <div className="form-group mb-4">
                                <input id="billing_street_2" type="text" autoComplete="address-line2" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_street_2')} aria-describedby={getError('billing_street_2') ? "billing_street_2-error" : undefined} {...register('billing_street_2')} />
                                <label htmlFor="billing_street_2" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                    Apt, Suite, etc.
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <FormInputError id="billing_city-error" message={getError('billing_city')} />
                                    <div className="form-group mb-4">
                                        <input id="billing_city" type="text" autoComplete="address-level2" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_city')} aria-describedby={getError('billing_city') ? "billing_city-error" : undefined} {...register('billing_city')} />
                                        <label htmlFor="billing_city" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                            City {!billingSameAsShipping && <span className="text-primary">*</span>}
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <FormInputError id="billing_state-error" message={getError('billing_state')} />
                                    <div className="form-group mb-4">
                                        <Controller name="billing_state" control={control} render={({
                    field: field_5
                  }) => <>
                                                    <select id="billing_state" autoComplete="address-level1" className="form-input peer" aria-invalid={!!getError('billing_state')} aria-describedby={getError('billing_state') ? "billing_state-error" : undefined} {...field_5}>
                                                        <option value=""></option>
                                                        {billingStates.map(state_0 => <option key={state_0.state_id} value={state_0.abbr}>
                                                                {state_0.state_name}
                                                            </option>)}
                                                    </select>
                                                    <label htmlFor="billing_state" className={`form-input-label ${!field_5.value ? 'form-input-label-placeholder-shown' : ''}`}>
                                                        State {!billingSameAsShipping && <span className="text-primary">*</span>}
                                                    </label>
                                                </>} />
                                    </div>
                                </div>

                                <div>
                                    <FormInputError id="billing_zip-error" message={getError('billing_zip')} />
                                    <div className="form-group mb-4">
                                        <input id="billing_zip" type="text" autoComplete="postal-code" inputMode="numeric" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_zip')} aria-describedby={getError('billing_zip') ? "billing_zip-error" : undefined} {...register('billing_zip')} />
                                        <label htmlFor="billing_zip" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                            Zip Code {!billingSameAsShipping && <span className="text-primary">*</span>}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <FormInputError id="billing_phone-error" message={getError('billing_phone')} />
                            <div className="form-group mb-4">
                                <Controller name="billing_phone" control={control} render={({
                field: field_6
              }) => <InputMask mask="(___) ___-____" replacement={{
                _: /\d/
              }} track={phoneTrack} id="billing_phone" type="tel" autoComplete="tel" inputMode="tel" className="form-input peer" placeholder=" " aria-invalid={!!getError('billing_phone')} aria-describedby={getError('billing_phone') ? "billing_phone-error" : undefined} value={field_6.value} onChange={field_6.onChange} onBlur={field_6.onBlur} />} />
                                <label htmlFor="billing_phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                    Phone {!billingSameAsShipping && <span className="text-primary">*</span>}
                                </label>
                            </div>

                            <div className="form-group mb-4">
                                <Controller name="alt_billing_phone" control={control} render={({
                field: field_7
              }) => <InputMask mask="(___) ___-____" replacement={{
                _: /\d/
              }} track={phoneTrack} id="alt_billing_phone" type="tel" autoComplete="tel" inputMode="tel" className="form-input peer" placeholder=" " value={field_7.value} onChange={field_7.onChange} onBlur={field_7.onBlur} />} />
                                <label htmlFor="alt_billing_phone" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                                    Alternate Phone
                                </label>
                            </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary whitespace-nowrap">
                            {isSubmitting ? 'Creating Account...' : 'Create Account'}
                        </button>
                        <div className="text-sm mt-4">
                            Already have an account?{' '}
                            <Link href="/account/login" className="underline hover:text-primary">
                                Sign In
                            </Link>
                        </div>
                    </div>

                    <div className="mt-4">
                        <RecaptchaDisclaimer />
                    </div>
                </form>
        </div>;
};
export default CreateAccountForm;